import React from "react";
import { Tag, Tooltip, Space } from "antd";
import {
  CheckCircleOutlined,
  StopOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined
} from "@ant-design/icons";

/**
 * Badge de statut produit avec couleurs et icônes
 * Affiche le statut actuel et fournit des informations contextuelles
 */
const ProductStatusBadge = ({ product, showDetails = false, size = "default" }) => {
  if (!product) return null;

  const status = product.status || "actif";
  const isDeleted = product.is_deleted === 1;
  const lastActivity = product.updated_at;
  const createdAt = product.created_at;

  const getStatusConfig = () => {
    if (isDeleted) {
      return {
        color: "red",
        icon: <ExclamationCircleOutlined />,
        label: "Supprimé",
        description: "Produit supprimé définitivement"
      };
    }

    switch (status) {
      case "actif":
        return {
          color: "green",
          icon: <CheckCircleOutlined />,
          label: "Actif",
          description: "Produit actif et disponible à la vente"
        };

      case "disabled":
        return {
          color: "orange",
          icon: <StopOutlined />,
          label: "Désactivé",
          description: "Ventes bloquées temporairement"
        };

      case "archived":
        return {
          color: "blue",
          icon: <InboxOutlined />,
          label: "Archivé",
          description: "Produit archivé mais données préservées"
        };

      case "draft":
        return {
          color: "default",
          icon: <ClockCircleOutlined />,
          label: "Brouillon",
          description: "Produit en cours de création"
        };

      case "out_of_stock":
        return {
          color: "volcano",
          icon: <WarningOutlined />,
          label: "Rupture",
          description: "Stock épuisé"
        };

      default:
        return {
          color: "default",
          icon: <ExclamationCircleOutlined />,
          label: status,
          description: "Statut non reconnu"
        };
    }
  };

  const config = getStatusConfig();

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getActivityInfo = () => {
    if (!lastActivity) return null;

    const daysSinceActivity = Math.floor(
      (Date.now() - new Date(lastActivity)) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceActivity === 0) return "Modifié aujourd'hui";
    if (daysSinceActivity === 1) return "Modifié hier";
    if (daysSinceActivity < 30) return `Modifié il y a ${daysSinceActivity} jours`;
    if (daysSinceActivity < 365) {
      const months = Math.floor(daysSinceActivity / 30);
      return `Modifié il y a ${months} mois`;
    }

    const years = Math.floor(daysSinceActivity / 365);
    return `Modifié il y a ${years} an${years > 1 ? 's' : ''}`;
  };

  const getCreationInfo = () => {
    if (!createdAt) return null;

    const daysSinceCreation = Math.floor(
      (Date.now() - new Date(createdAt)) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceCreation === 0) return "Créé aujourd'hui";
    if (daysSinceCreation === 1) return "Créé hier";
    if (daysSinceCreation < 30) return `Créé il y a ${daysSinceCreation} jours`;
    if (daysSinceCreation < 365) {
      const months = Math.floor(daysSinceCreation / 30);
      return `Créé il y a ${months} mois`;
    }

    const years = Math.floor(daysSinceCreation / 365);
    return `Créé il y a ${years} an${years > 1 ? 's' : ''}`;
  };

  const getTooltipContent = () => {
    const parts = [config.description];

    const activityInfo = getActivityInfo();
    if (activityInfo) {
      parts.push(activityInfo);
    }

    const creationInfo = getCreationInfo();
    if (creationInfo) {
      parts.push(creationInfo);
    }

    // Informations supplémentaires produit
    if (product.item_code) {
      parts.push(`Code: ${product.item_code}`);
    }

    return parts.join('\n');
  };

  const badge = (
    <Tag
      color={config.color}
      icon={config.icon}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: size === "small" ? 12 : 14,
        borderRadius: 6
      }}
    >
      {config.label}
    </Tag>
  );

  if (!showDetails) {
    return (
      <Tooltip title={getTooltipContent()} placement="top">
        {badge}
      </Tooltip>
    );
  }

  return (
    <Space direction="vertical" size={2}>
      <Tooltip title={getTooltipContent()} placement="top">
        {badge}
      </Tooltip>

      {showDetails && (
        <div style={{ fontSize: 12, color: '#666' }}>
          {getActivityInfo() && (
            <div>{getActivityInfo()}</div>
          )}
          {getCreationInfo() && (
            <div>{getCreationInfo()}</div>
          )}
          {product.item_code && (
            <div>Code: {product.item_code}</div>
          )}
        </div>
      )}
    </Space>
  );
};

/**
 * Badge spécialisé pour l'état de stock
 */
export const ProductStockBadge = ({ stock, alertLimit = 0 }) => {
  if (stock === null || stock === undefined) {
    return (
      <Tag color="default" icon={<ExclamationCircleOutlined />}>
        Stock inconnu
      </Tag>
    );
  }

  let config;

  if (stock <= 0) {
    config = {
      color: "red",
      icon: <WarningOutlined />,
      label: stock === 0 ? "Rupture" : `Stock: ${stock}`,
      description: stock === 0 ? "Stock épuisé" : "Stock négatif - Anomalie détectée"
    };
  } else if (stock <= alertLimit) {
    config = {
      color: "orange",
      icon: <WarningOutlined />,
      label: `Stock: ${stock}`,
      description: `Stock faible (seuil: ${alertLimit})`
    };
  } else {
    config = {
      color: "green",
      icon: <CheckCircleOutlined />,
      label: `Stock: ${stock}`,
      description: "Stock disponible"
    };
  }

  return (
    <Tooltip title={config.description}>
      <Tag color={config.color} icon={config.icon} style={{ fontSize: 12 }}>
        {config.label}
      </Tag>
    </Tooltip>
  );
};

/**
 * Badge pour les ventes/activité du produit
 */
export const ProductActivityBadge = ({ salesCount = 0, lastSaleDate = null }) => {
  let config;

  if (salesCount === 0) {
    config = {
      color: "default",
      icon: <ClockCircleOutlined />,
      label: "Jamais vendu",
      description: "Aucune vente enregistrée"
    };
  } else if (salesCount === 1) {
    config = {
      color: "blue",
      icon: <CheckCircleOutlined />,
      label: "1 vente",
      description: lastSaleDate ? `Dernière vente: ${new Date(lastSaleDate).toLocaleDateString('fr-FR')}` : "1 vente enregistrée"
    };
  } else if (salesCount < 10) {
    config = {
      color: "cyan",
      icon: <CheckCircleOutlined />,
      label: `${salesCount} ventes`,
      description: lastSaleDate ? `Dernière vente: ${new Date(lastSaleDate).toLocaleDateString('fr-FR')}` : `${salesCount} ventes enregistrées`
    };
  } else {
    config = {
      color: "green",
      icon: <CheckCircleOutlined />,
      label: `${salesCount} ventes`,
      description: lastSaleDate ? `Dernière vente: ${new Date(lastSaleDate).toLocaleDateString('fr-FR')}` : `Produit populaire (${salesCount} ventes)`
    };
  }

  return (
    <Tooltip title={config.description}>
      <Tag color={config.color} icon={config.icon} style={{ fontSize: 12 }}>
        {config.label}
      </Tag>
    </Tooltip>
  );
};

export default ProductStatusBadge;